// src/features/operational/components/teaching/LessonCalendar.js
import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Chip,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import {
  School as SchoolIcon,
  Add as AddIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  AssignmentTurnedIn as AssignmentIcon,
  BookmarkBorder as HomeworkIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';

const LessonCalendar = ({ lessons = [], currentWeek, onAddLesson }) => {
  // Generate the days of the week based on currentWeek
  const weekDays = getWeekDays(currentWeek.start);
  
  // Group lessons by day
  const lessonsByDay = groupLessonsByDay(lessons);
  
  return (
    <Box>
      <Grid container spacing={2}>
        {weekDays.map((day) => (
          <Grid key={day.date} item sx={{ width: { xs: '100%', md: '48%' } }}>
            <Paper 
              elevation={1} 
              sx={{ 
                height: '100%', 
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box 
                sx={{ 
                  bgcolor: day.isToday ? THEME_COLORS.primary : 'rgba(0, 0, 0, 0.04)',
                  color: day.isToday ? 'white' : 'text.primary',
                  p: 1.5,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8
                }}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  {day.name}
                </Typography>
                <Typography variant="body2">
                  {day.dateStr}
                </Typography>
              </Box>
              
              <Box sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {lessonsByDay[day.date]?.length > 0 ? (
                  <Box sx={{ flexGrow: 1 }}>
                    {lessonsByDay[day.date].map((lesson) => (
                      <LessonCard key={lesson.id} lesson={lesson} />
                    ))}
                  </Box>
                ) : (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      minHeight: 120
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      No lessons scheduled
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={onAddLesson}
                      sx={{
                        borderColor: '#ed1566',
                        color: THEME_COLORS.primary,
                        '&:hover': {
                          borderColor: '#1e1c4e',
                          backgroundColor: 'rgba(39, 35, 97, 0.04)'
                        }
                      }}
                    >
                      Add Lesson
                    </Button>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Helper component for lesson cards
const LessonCard = ({ lesson }) => {
  return (
    <Card 
      variant="outlined" 
      sx={{ 
        mb: 2, 
        borderRadius: 2, 
        borderLeft: '4px solid',
        borderColor: lesson.atpStatus === 'Ahead' ? '#4caf50' : 
                    lesson.atpStatus === 'Behind' ? '#f44336' : '#ff9800',
        '&:last-child': { mb: 0 }
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="flex-start" mb={0.5}>
          <ScheduleIcon fontSize="small" sx={{ mr: 0.5, mt: 0.3, color: 'text.secondary' }} />
          <Typography variant="body2" fontWeight="medium" sx={{ flexGrow: 1 }}>
            {lesson.time}
          </Typography>
        </Box>
        
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {lesson.topic}
        </Typography>
        
        <Box display="flex" alignItems="center" mb={0.5}>
          <SchoolIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {lesson.subject.name} - Grade {lesson.grade}
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" mb={0.5}>
          <GroupsIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            {lesson.actualAttendance}/{lesson.expectedAttendance} learners
          </Typography>
        </Box>
        
        <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
          {lesson.assessmentType && (
            <Chip 
              label={lesson.assessmentType} 
              size="small"
              icon={<AssignmentIcon fontSize="small" />}
              sx={{ 
                height: 20, 
                '& .MuiChip-label': { px: 1, fontSize: '0.625rem' },
                '& .MuiChip-icon': { fontSize: '0.75rem' },
                bgcolor: 'rgba(39, 35, 97, 0.08)'
              }}
            />
          )}
          
          {lesson.hasHomework && (
            <Chip 
              label="Homework" 
              size="small"
              icon={<HomeworkIcon fontSize="small" />}
              sx={{ 
                height: 20, 
                '& .MuiChip-label': { px: 1, fontSize: '0.625rem' },
                '& .MuiChip-icon': { fontSize: '0.75rem' },
                bgcolor: 'rgba(244, 67, 54, 0.08)',
                color: '#f44336'
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// Helper functions
function getWeekDays(startDateStr) {
  const startDate = new Date(startDateStr);
  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    
    const isToday = date.getTime() === today.getTime();
    
    days.push({
      name: date.toLocaleDateString('en-US', { weekday: 'long' }),
      dateStr: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      date: date.toISOString().split('T')[0],
      isToday
    });
  }
  
  return days;
}

function groupLessonsByDay(lessons) {
  return lessons.reduce((acc, lesson) => {
    const day = lesson.date;
    if (!acc[day]) {
      acc[day] = [];
    }
    acc[day].push(lesson);
    return acc;
  }, {});
}

export default LessonCalendar;
